/**
 * features/burs/ITW8Certificates.tsx
 * ------------------------------------------------------------------
 * BURS — ITW-8 employee annual tax certificates. Pick a tax year
 * (1 Jul – 30 Jun); lists each employee's annual gross + tax with a
 * per-employee certificate PDF and a "generate all" (one page each).
 * Due to staff by 31 March. Renders embedded in the BURS hub.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { FileText, Files, AlertTriangle, Search } from 'lucide-react';
import { SoftCard, StatChip, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useHrData } from '@/features/hr/useHrData';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { fmtPula } from '@/lib/hrPayroll';
import {
  buildAnnualByEmployee,
  resolveEmployerDetails,
  taxYear,
  taxYearEndYearOf,
  type EmployeeAnnualRow,
} from '@/lib/bursForms';
import { buildItw8Pdf } from '@/lib/bursPdf';

export default function ITW8Certificates() {
  const data = useHrData();
  const { userData } = useAuth();
  const defaultEnd = taxYearEndYearOf({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [endYear, setEndYear] = useState(defaultEnd);
  const [search, setSearch] = useState('');
  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => { loadLogoDataUrl().then(setLogo); }, []);

  const ty = taxYear(endYear);
  const rows = useMemo(() => buildAnnualByEmployee(data.payslips, endYear), [data.payslips, endYear]);
  const emp = resolveEmployerDetails(data.payrollSettings);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q) || r.employeeNo.toLowerCase().includes(q));
  }, [rows, search]);

  const opts = { logoDataUrl: logo, preparedBy: userData?.displayName };

  function downloadOne(row: EmployeeAnnualRow) {
    buildItw8Pdf([row], ty, emp, opts).save(`ITW-8_${row.employeeNo}_${endYear}.pdf`);
  }
  function downloadAll() {
    buildItw8Pdf(rows, ty, emp, opts).save(`ITW-8_all_${endYear}.pdf`);
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  const yearOptions = [defaultEnd + 1, defaultEnd, defaultEnd - 1, defaultEnd - 2];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-500 dark:text-gray-400">{ty.label} · annual tax certificate per employee · due to staff by 31 March</p>
        <select value={endYear} onChange={(e) => setEndYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
          {yearOptions.map((y) => <option key={y} value={y}>{`FY ending 30 Jun ${y}`}</option>)}
        </select>
      </div>

      {rows.length === 0 ? (
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
            <AlertTriangle size={16} />
            No payslips in {ty.label}. Generate the monthly pay runs first; certificates aggregate from them.
          </div>
        </SoftCard>
      ) : (
        <SoftCard pad="p-0" hover={false}>
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <StatChip tone="slate" value={String(rows.length)} label="employees" />
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search employee…"
                  className={glassFieldClass('py-1.5 text-sm pl-8')}
                />
              </div>
            </div>
            <button onClick={downloadAll} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
              <Files size={15} /> Generate all ({rows.length})
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 px-4 font-medium">Employee No</th>
                  <th className="py-2 px-4 font-medium">Name</th>
                  <th className="py-2 px-4 font-medium text-right">Months</th>
                  <th className="py-2 px-4 font-medium text-right">Gross</th>
                  <th className="py-2 px-4 font-medium text-right">Total tax</th>
                  <th className="py-2 px-4 font-medium text-right">Certificate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {filtered.map((r) => (
                  <tr key={r.staffId}>
                    <td className="py-2 px-4 text-gray-500 dark:text-gray-400">#{r.employeeNo}</td>
                    <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{r.name}</td>
                    <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">{r.monthsPaid}</td>
                    <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(r.gross)}</td>
                    <td className="py-2 px-4 text-right font-medium text-gray-900 dark:text-white">{fmtPula(r.totalTax)}</td>
                    <td className="py-2 px-4 text-right">
                      <button onClick={() => downloadOne(r)} className="inline-flex items-center gap-1 text-[var(--accent-link,#2d5295)] hover:underline">
                        <FileText size={14} /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SoftCard>
      )}
    </div>
  );
}
